import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '../shared/api/HttpClient';
import { cartModeLabel, connectionQualityLabel, engineStateLabel, itemTypeLabel, sourceKindLabel, stationModeLabel } from '../shared/i18n';
import type { TrackSummary } from '../segue/types';
import { StudioApi } from './StudioApi';
import { canRunCommand, formatDuration, isEndingSoon, projectTiming, shortcutLabel, upcomingQueue, waveformBars } from './studioModel';
import type { CartItem, StationSummary, StudioCommandType, StudioSnapshot, StudioTrack } from './types';

interface CommandIntent {
  key: string;
  type: StudioCommandType;
  revision: number;
  parameters: Record<string, string | number | boolean | null>;
}

export function DesktopStudio(): ReactElement {
  const api = useMemo(() => new StudioApi(), []);
  const [stations, setStations] = useState<StationSummary[]>([]);
  const [stationId, setStationId] = useState('');
  const [snapshot, setSnapshot] = useState<StudioSnapshot | null>(null);
  const [receivedAt, setReceivedAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<StudioCommandType | null>(null);
  const [retryIntent, setRetryIntent] = useState<CommandIntent | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cueItemId, setCueItemId] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryTracks, setLibraryTracks] = useState<TrackSummary[]>([]);
  const [libraryQuery, setLibraryQuery] = useState('');
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [insertPending, setInsertPending] = useState<string | null>(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginPending, setLoginPending] = useState(false);
  const cueAudio = useRef<HTMLAudioElement | null>(null);

  const loadSnapshot = useCallback(async (id: string, quiet = false): Promise<void> => {
    try {
      const next = await api.snapshot(id);
      setSnapshot(next);
      setReceivedAt(Date.now());
      if (!quiet) setError(null);
    } catch (reason: unknown) {
      if (!quiet) setError(problemMessage(reason));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [api]);

  const loadStations = useCallback(async (): Promise<void> => {
    try {
      const items = await api.stations();
      setStations(items);
      const requested = new URLSearchParams(window.location.search).get('station');
      const initial = items.find((station) => station.id === requested)?.id ?? items[0]?.id ?? '';
      setStationId(initial);
      if (initial === '') setLoading(false);
      setAuthRequired(false);
    } catch (reason: unknown) {
      if (reason instanceof ApiError && reason.problem.status === 401) {
        setAuthRequired(true);
        setError(null);
        setLoading(false);
      } else {
        setError(problemMessage(reason));
        setLoading(false);
      }
    }
  }, [api]);

  useEffect(() => { void loadStations(); }, [loadStations]);

  useEffect(() => {
    if (stationId === '') return;
    let active = true;
    let firstPoll = true;
    let timer = 0;
    setLoading(true);
    setSnapshot(null);
    const poll = async (): Promise<void> => {
      await loadSnapshot(stationId, !active || !firstPoll);
      firstPoll = false;
      if (active) timer = window.setTimeout(() => { void poll(); }, 1500);
    };
    void poll();
    return () => { active = false; window.clearTimeout(timer); };
  }, [loadSnapshot, stationId]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, []);

  const execute = useCallback(async (intent: CommandIntent): Promise<void> => {
    if (stationId === '' || pending !== null) return;
    setPending(intent.type);
    setError(null);
    setNotice(null);
    try {
      const receipt = await api.command(stationId, intent.type, intent.revision, intent.parameters, intent.key);
      if (receipt.state !== 'SUCCEEDED') throw new Error('Radio Engine не підтвердив виконання команди.');
      setRetryIntent(null);
      setNotice(commandNotice(intent.type, receipt.replayed));
      await loadSnapshot(stationId, true);
    } catch (reason: unknown) {
      setRetryIntent(reason instanceof ApiError ? null : intent);
      setError(problemMessage(reason));
      if (reason instanceof ApiError && reason.problem.code === 'runtime_revision_mismatch') {
        await loadSnapshot(stationId, true);
      }
    } finally {
      setPending(null);
    }
  }, [api, loadSnapshot, pending, stationId]);

  const runCommand = useCallback((type: StudioCommandType, parameters: CommandIntent['parameters'] = {}): void => {
    if (snapshot === null) return;
    void execute({ key: crypto.randomUUID(), type, revision: snapshot.runtime.revision, parameters });
  }, [execute, snapshot]);

  useEffect(() => {
    function handleShortcut(event: KeyboardEvent): void {
      if (event.repeat || event.altKey || event.ctrlKey || event.metaKey || snapshot === null || pending !== null) return;
      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return;
      const cart = snapshot.cartWall.items.find((item) => item.keyboardShortcut === event.code);
      if (cart === undefined || !snapshot.cartWall.enabled || !canRunCommand(snapshot, 'CART_TRIGGER')) return;
      event.preventDefault();
      runCommand('CART_TRIGGER', { cartItemId: cart.id });
    }
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [pending, runCommand, snapshot]);

  const timing = snapshot === null ? null : projectTiming(snapshot, receivedAt, now);
  const station = stations.find((item) => item.id === stationId) ?? null;
  const onAir = snapshot?.runtime.engineState === 'ON_AIR';
  const assist = snapshot?.runtime.effectiveMode === 'ASSIST';
  const endingSoon = isEndingSoon(timing?.remainingMs ?? null);
  const queuedItems = snapshot === null ? [] : upcomingQueue(snapshot.queue);
  const nextQueueItemId = queuedItems.find((item) => item.isNext)?.id
    ?? queuedItems[0]?.id
    ?? null;
  const visibleLibraryTracks = useMemo(() => {
    const query = libraryQuery.trim().toLocaleLowerCase('uk-UA');
    const matched = query === '' ? libraryTracks : libraryTracks.filter((track) =>
      [track.artist, track.title, track.album ?? '', track.genre ?? '']
        .some((value) => value.toLocaleLowerCase('uk-UA').includes(query)),
    );
    return matched.slice(0, 30);
  }, [libraryQuery, libraryTracks]);

  async function toggleCue(trackId: string, cueKey: string): Promise<void> {
    const audio = cueAudio.current;
    if (audio === null) return;
    if (cueItemId === cueKey && !audio.paused) {
      audio.pause();
      setCueItemId(null);
      return;
    }
    audio.pause();
    audio.src = api.audioUrl(trackId);
    try {
      await audio.play();
      setCueItemId(cueKey);
    } catch {
      setCueItemId(null);
      setError('CUE preview не запустився. Перевірте доступ до бібліотеки й аудіовихід браузера.');
    }
  }

  async function toggleLibrary(): Promise<void> {
    const opening = !libraryOpen;
    setLibraryOpen(opening);
    if (!opening || libraryTracks.length > 0 || libraryLoading) return;
    setLibraryLoading(true);
    setError(null);
    try {
      setLibraryTracks(await api.tracks());
    } catch (reason: unknown) {
      setError(problemMessage(reason));
    } finally {
      setLibraryLoading(false);
    }
  }

  async function insertIntoQueue(track: TrackSummary): Promise<void> {
    if (snapshot === null || stationId === '' || insertPending !== null) return;
    setInsertPending(track.id);
    setError(null);
    setNotice(null);
    const serverNow = Date.parse(snapshot.runtime.serverTime);
    const expectedEnd = snapshot.runtime.expectedEndAt === null ? Number.NaN : Date.parse(snapshot.runtime.expectedEndAt);
    const scheduledAt = new Date(Math.max(serverNow + 5_000, Number.isNaN(expectedEnd) ? 0 : expectedEnd)).toISOString();
    try {
      await api.createQueueInsert(stationId, track.id, scheduledAt);
      setNotice(`«${track.artist} — ${track.title}» додано до черги. Планувальник поставить матеріал у найближчу безпечну позицію.`);
      await loadSnapshot(stationId, true);
    } catch (reason: unknown) {
      setError(problemMessage(reason));
    } finally {
      setInsertPending(null);
    }
  }

  async function submitLogin(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (loginPending) return;
    setLoginPending(true);
    setError(null);
    try {
      await api.login(loginEmail, loginPassword);
      setLoginPassword('');
      setLoading(true);
      await loadStations();
    } catch (reason: unknown) {
      setError(reason instanceof ApiError && reason.problem.status === 401
        ? 'Електронну пошту або пароль не прийнято.'
        : problemMessage(reason));
    } finally {
      setLoginPending(false);
    }
  }

  return (
    <div className="studio-room">
      <aside className="studio-rail">
        <a className="studio-brand" href="/studio" aria-label="Ефірна студія SweetLana"><span>SL</span><small>СТУДІЯ</small></a>
        <nav aria-label="Навігація ефірної студії">
          <a className="studio-rail-link active" href="/studio"><span>●</span><small>Ефір</small></a>
          <a className="studio-rail-link" href="/admin/scheduler"><span>≡</span><small>План</small></a>
          <a className="studio-rail-link" href="/admin/library"><span>⌁</span><small>Медіатека</small></a>
        </nav>
        <span className="studio-build">M6</span>
      </aside>

      <main className="studio-shell">
        <header className="studio-topbar">
          <div className={`on-air-block ${onAir ? 'live' : ''}`}>
            <span className="on-air-lamp" aria-hidden="true" />
            <div><small>ПЕРЕДАВАННЯ</small><strong>{onAir ? 'В ЕФІРІ' : engineStateLabel(snapshot?.runtime.engineState ?? 'OFFLINE')}</strong></div>
          </div>
          <div className="studio-station-picker">
            <label htmlFor="studio-station">Станція</label>
            <select id="studio-station" value={stationId} onChange={(event) => setStationId(event.target.value)}>
              {stations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>
          <div className="studio-mode-readout">
            <div className="studio-mode-copy"><small>РЕЖИМ</small><strong className={assist ? 'assist' : ''}>{snapshot === null ? '—' : stationModeLabel(snapshot.runtime.effectiveMode)}</strong><span>версія {snapshot?.runtime.revision ?? '—'}</span></div>
            {snapshot !== null && (assist ? (
              <button type="button" className="studio-mode-action return" disabled={!canRunCommand(snapshot, 'RETURN_AUTO') || pending !== null} onClick={() => runCommand('RETURN_AUTO')}>ПОВЕРНУТИ АВТОЕФІР</button>
            ) : (
              <button type="button" className="studio-mode-action" disabled={!canRunCommand(snapshot, 'TAKE_CONTROL') || pending !== null} onClick={() => runCommand('TAKE_CONTROL')}>ВЗЯТИ КЕРУВАННЯ</button>
            ))}
          </div>
          <div className={`studio-link-quality quality-${snapshot?.runtime.connectionQuality.toLowerCase() ?? 'offline'}`}>
            <span aria-hidden="true" />
            <div><small>ЗВ’ЯЗОК ІЗ СЕРВЕРОМ</small><strong>{connectionQualityLabel(snapshot?.runtime.connectionQuality ?? 'OFFLINE')}</strong></div>
          </div>
          <time className="studio-clock">{new Date(now).toLocaleTimeString('uk-UA', { hour12: false })}</time>
        </header>

        {error !== null && <div className="studio-alert" role="alert"><strong>Увага</strong><span>{error}</span>{retryIntent !== null && <button type="button" onClick={() => void execute(retryIntent)}>Повторити ту саму команду</button>}</div>}
        {notice !== null && <div className="studio-notice" role="status">{notice}</div>}

        {authRequired ? (
          <section className="studio-login" aria-labelledby="studio-login-title">
            <form onSubmit={(event) => void submitLogin(event)}>
              <p className="eyebrow">ЗАХИЩЕНЕ КЕРУВАННЯ</p>
              <h1 id="studio-login-title">Вхід до ефірної студії</h1>
              <p>Використайте локальний обліковий запис цієї інсталяції. Дані не передаються зовнішнім сервісам.</p>
              <label htmlFor="studio-login-email">Електронна пошта</label>
              <input id="studio-login-email" name="email" type="email" autoComplete="username" required value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} />
              <label htmlFor="studio-login-password">Пароль</label>
              <input id="studio-login-password" name="password" type="password" autoComplete="current-password" required value={loginPassword} onChange={(event) => setLoginPassword(event.target.value)} />
              <button type="submit" disabled={loginPending}>{loginPending ? 'ПЕРЕВІРКА…' : 'УВІЙТИ ДО СТУДІЇ'}</button>
              <small>Сесія зберігається у захищеному HttpOnly cookie; пароль — ні.</small>
            </form>
          </section>
        ) : loading && snapshot === null ? (
          <section className="studio-empty"><span className="studio-spinner" /><h1>Підключення до ефірної студії</h1><p>Завантажую стан ефіру, активний план і панель швидких аудіовставок.</p></section>
        ) : snapshot === null ? (
          <section className="studio-empty"><h1>{stations.length === 0 ? 'Немає доступної станції' : 'Ефірна студія недоступна'}</h1><p>Для роботи потрібне право перегляду цієї станції та активний серверний ефір.</p></section>
        ) : (
          <div className="studio-workspace">
            <section className="studio-programme" aria-label="Поточний і наступний матеріал">
              <div className="deck deck-current">
                <div className="deck-heading">
                  <div><p className="eyebrow">ЗАРАЗ / {snapshot.current === null ? 'НЕМАЄ МАТЕРІАЛУ' : itemTypeLabel(snapshot.current.itemType)}</p><h1>{snapshot.current?.title ?? 'Очікування джерела'}</h1><p>{snapshot.current?.artist ?? station?.name ?? 'SweetLana'}</p></div>
                  <div className={`remaining ${endingSoon ? 'ending' : ''}`}><small>ЗАЛИШИЛОСЯ</small><strong>{formatDuration(timing?.remainingMs ?? null)}</strong></div>
                </div>
                <Waveform track={snapshot.current} progress={timing?.progress ?? 0} />
                <div className="cue-countdowns">
                  <Countdown label="ІНТРО" value={timing?.introMs ?? null} />
                  <Countdown label="ВОКАЛ" value={timing?.vocalMs ?? null} />
                  <Countdown label="АУТРО" value={timing?.outroMs ?? null} active={snapshot.current !== null && snapshot.current.cuePoints.outroStartMs !== null && timing !== null && timing.outroMs === null && timing.elapsedMs >= snapshot.current.cuePoints.outroStartMs} />
                  <div><small>МИНУЛО</small><strong>{formatDuration(timing?.elapsedMs ?? null)}</strong></div>
                </div>
              </div>

              <audio ref={cueAudio} onEnded={() => setCueItemId(null)} />

              <div className="queue-panel">
                <header>
                  <div><p className="eyebrow">АКТИВНИЙ ПЛАН</p><h2>Черга ефіру</h2></div>
                  <div className="queue-header-actions"><span>{queuedItems.length} позицій</span><button type="button" className={libraryOpen ? 'active' : ''} onClick={() => void toggleLibrary()}>{libraryOpen ? 'ЗАКРИТИ МЕДІАТЕКУ' : '+ ДОДАТИ З МЕДІАТЕКИ'}</button></div>
                </header>
                {libraryOpen && (
                  <section className="queue-library" aria-label="Пошук у медіатеці">
                    <div className="queue-library-search">
                      <label htmlFor="studio-library-search">Пошук пісні</label>
                      <input id="studio-library-search" type="search" autoFocus placeholder="Назва, виконавець, альбом або жанр" value={libraryQuery} onChange={(event) => setLibraryQuery(event.target.value)} />
                      <span>{libraryTracks.length === 0 ? '—' : `${visibleLibraryTracks.length} із ${libraryTracks.length}`}</span>
                    </div>
                    <div className="queue-library-results">
                      {libraryLoading && <p>Завантажую медіатеку…</p>}
                      {!libraryLoading && visibleLibraryTracks.length === 0 && <p>{libraryTracks.length === 0 ? 'У медіатеці немає готових матеріалів.' : 'За цим запитом нічого не знайдено.'}</p>}
                      {visibleLibraryTracks.map((track) => {
                        const cueKey = `library-${track.id}`;
                        return (
                          <div className="queue-library-item" key={track.id}>
                            <span className="library-type">{itemTypeLabel(track.itemType)}</span>
                            <div><strong>{track.title}</strong><small>{track.artist}{track.album === null ? '' : ` · ${track.album}`}</small></div>
                            <time>{formatDuration(track.durationMs)}</time>
                            <button type="button" className={cueItemId === cueKey ? 'active' : ''} onClick={() => void toggleCue(track.id, cueKey)}>{cueItemId === cueKey ? 'СТОП' : 'ПРОСЛУХАТИ'}</button>
                            <button type="button" className="insert" disabled={insertPending !== null} onClick={() => void insertIntoQueue(track)}>{insertPending === track.id ? 'ДОДАЮ…' : 'ДОДАТИ ДО ЧЕРГИ'}</button>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}
                <div className="queue-ledger">
                  {queuedItems.length === 0 && <p className="queue-empty">Наступних матеріалів у черзі поки немає.</p>}
                  {queuedItems.map((item, index) => {
                    const actionTarget = item.id === nextQueueItemId;
                    const selectedCurrent = item.isCurrent || item.state === 'PLAYING';
                    const transportTitle = selectedCurrent ? 'Цей матеріал уже звучить в ефірі.' : undefined;
                    return (
                    <div className={`queue-item ${item.isCurrent ? 'playing' : item.state.toLowerCase()} ${actionTarget ? 'action-target' : ''}`} key={item.id}>
                      <time>{new Date(item.scheduledAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</time>
                      <span className="queue-index">{String(index + 1).padStart(2, '0')}</span>
                      <div className="queue-copy"><strong>{item.title}</strong><small>{item.artist}</small></div>
                      <span className={`queue-intro ${item.introDurationMs === null ? 'empty' : ''}`}>{item.introDurationMs === null ? 'БЕЗ ІНТРО' : <>ІНТРО <strong>{formatDuration(item.introDurationMs)}</strong></>}</span>
                      <span className="queue-context"><i>{itemTypeLabel(item.itemType)}</i><i>{sourceKindLabel(item.sourceKind)}</i></span>
                      <strong className="queue-time">{formatDuration(item.expectedDurationMs)}</strong>
                      <div className="queue-actions">
                        <button type="button" className={cueItemId === item.id ? 'active' : ''} onClick={() => void toggleCue(item.trackId, item.id)}>{cueItemId === item.id ? 'СТОП' : 'ПРОСЛУХАТИ'}</button>
                        {snapshot.current === null ? (
                          <button type="button" className="air-now" title={transportTitle} disabled={selectedCurrent || !canRunCommand(snapshot, 'PLAY') || pending !== null} onClick={() => runCommand('PLAY', { queueItemId: item.id })}>ЗАПУСТИТИ</button>
                        ) : (
                          <>
                            <button type="button" className="air-now" title={transportTitle} disabled={selectedCurrent || !canRunCommand(snapshot, 'NEXT') || pending !== null} onClick={() => runCommand('NEXT', { queueItemId: item.id })}>В ЕФІР ЗАРАЗ</button>
                            <button type="button" className="fade-next" title={transportTitle} disabled={selectedCurrent || !canRunCommand(snapshot, 'FADE_NOW') || pending !== null} onClick={() => runCommand('FADE_NOW', { queueItemId: item.id })}>ЧЕРЕЗ ФЕЙД</button>
                          </>
                        )}
                      </div>
                    </div>
                  );})}
                </div>
              </div>
            </section>

            <section className="cart-wall" aria-label="Панель швидких аудіовставок">
              <header><div><p className="eyebrow">ШВИДКІ АУДІОВСТАВКИ</p><h2>{cartWallName(snapshot.cartWall.name)}</h2></div><div className="cart-legend"><span>О Одразу</span><span>Ч У чергу</span><span>Н Накласти</span><span>П На переході</span></div></header>
              <div className="cart-grid">
                {snapshot.cartWall.items.length === 0 && <p className="cart-empty">Панель швидких аудіовставок ще не налаштована. Менеджер станції може створити до 24 кнопок.</p>}
                {snapshot.cartWall.items.map((item) => <CartButton key={item.id} item={item} busy={pending === 'CART_TRIGGER'} disabled={!snapshot.cartWall.enabled || !canRunCommand(snapshot, 'CART_TRIGGER')} onTrigger={() => runCommand('CART_TRIGGER', { cartItemId: item.id })} />)}
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

function Waveform({ track, progress, compact = false }: { track: StudioTrack | null; progress: number; compact?: boolean }): ReactElement {
  const bars = waveformBars(track, compact ? 72 : 180);
  return (
    <div className={`studio-waveform ${compact ? 'compact' : ''}`} style={{ '--playhead': `${Math.round(progress * 10000) / 100}%` } as CSSProperties} aria-label={track === null ? 'Немає форми хвилі' : `Форма хвилі: ${track.title}`}>
      <div className="waveform-bars" aria-hidden="true">{bars.map((value, index) => <i key={index} style={{ height: `${Math.round(value * 88 + 8)}%` }} />)}</div>
      <span className="waveform-played" aria-hidden="true" />
      {!compact && track !== null && <div className="waveform-markers" aria-hidden="true"><Marker label="СТАРТ" at={track.cuePoints.cueInMs / track.durationMs} /><Marker label="ВОК" at={(track.cuePoints.vocalStartMs ?? track.cuePoints.introEndMs ?? 0) / track.durationMs} /><Marker label="ФІНІШ" at={(track.cuePoints.outroStartMs ?? track.durationMs) / track.durationMs} /></div>}
    </div>
  );
}

function Marker({ label, at }: { label: string; at: number }): ReactElement {
  return <span style={{ left: `${Math.max(0, Math.min(1, at)) * 100}%` }}><i />{label}</span>;
}

function Countdown({ label, value, active = false }: { label: string; value: number | null; active?: boolean }): ReactElement {
  return <div className={active ? 'active' : ''}><small>{label}</small><strong>{value === null ? (active ? 'ЗАРАЗ' : '—') : formatDuration(value)}</strong></div>;
}

function CartButton({ item, disabled, busy, onTrigger }: { item: CartItem; disabled: boolean; busy: boolean; onTrigger: () => void }): ReactElement {
  return (
    <button type="button" className={`cart-button mode-${item.triggerMode.toLowerCase().replace('_', '-')}`} disabled={disabled || busy} onClick={onTrigger}>
      <span className="cart-mode">{cartModeLabel(item.triggerMode)}</span>
      <strong>{item.label}</strong>
      <small>{item.title}</small>
      <span className="cart-foot"><i>{formatDuration(item.durationMs)}</i>{item.keyboardShortcut !== null && <kbd>{shortcutLabel(item.keyboardShortcut)}</kbd>}</span>
    </button>
  );
}

function problemMessage(reason: unknown): string {
  if (reason instanceof ApiError) {
    if (reason.problem.status === 401) return 'Сесія відсутня або завершилась. Увійдіть у панель керування.';
    if (reason.problem.code === 'runtime_revision_mismatch') return 'Runtime уже змінився. Дані оновлено — повторіть дію після перевірки ефіру.';
    return reason.problem.detail ?? reason.problem.title;
  }
  return reason instanceof Error ? reason.message : 'Ефірна студія не змогла завершити запит.';
}

function cartWallName(name: string): string { return name === 'Cart Wall' ? 'Панель швидких аудіовставок' : name; }

function commandNotice(type: StudioCommandType, replayed: boolean): string {
  const labels: Record<StudioCommandType, string> = {
    TAKE_CONTROL: 'Ручне керування активовано.', RETURN_AUTO: 'Керування повернено автоматичному ефіру.', PLAY: 'Запуск джерела підтверджено.',
    NEXT: 'Перехід до наступного матеріалу підтверджено.', FADE_NOW: 'Негайний фейд підтверджено.', CART_TRIGGER: 'Швидку аудіовставку підтверджено сервером.',
  };
  return `${labels[type]}${replayed ? ' Повернено раніше збережений результат — повторного виконання не було.' : ''}`;
}
